// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ChunkParser, StreamCallback } from "../types"

/**
 * Reads an SSE response stream, passing each line to the parser.
 *
 * @param response - The fetch Response whose body is an SSE stream.
 * @param parser   - Parser that converts each raw SSE line into stream events.
 * @param callback - Invoked with each parsed stream event.
 * @param signal   - Optional AbortSignal. When aborted, stream reading stops
 *                   promptly and the underlying reader is cancelled, letting
 *                   callers cancel an in-flight stream.
 * @returns A promise that resolves once the stream is fully read or aborted.
 */
export async function readSSEStream(
  response: Response,
  parser: ChunkParser,
  callback: StreamCallback,
  signal?: AbortSignal
): Promise<void> {
  let buffer = ""

  if (!response.body) {
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()

  // Cancel the reader as soon as the signal aborts so a paused `reader.read()`
  // rejects and we exit the loop instead of continuing to emit stale events.
  const onAbort = (): void => {
    // `cancel` rejects the pending read; swallow the resulting error since an
    // intentional abort is not a failure condition.
    void reader.cancel().catch(() => undefined)
  }
  if (signal) {
    if (signal.aborted) {
      onAbort()
    } else {
      signal.addEventListener("abort", onAbort, { once: true })
    }
  }

  try {
    while (true) {
      // Stop before reading more if we've been aborted mid-stream.
      if (signal?.aborted) break

      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      const lines = buffer.split("\n")
      buffer = lines.pop() || ""

      for (const line of lines) {
        if (signal?.aborted) break
        if (line.trim()) {
          parser(line, callback)
        }
      }
    }

    // Process any remaining data in the buffer (unless aborted).
    if (!signal?.aborted && buffer.trim()) {
      parser(buffer, callback)
    }
  } finally {
    if (signal) signal.removeEventListener("abort", onAbort)
    // `releaseLock` throws if the reader was already cancelled by the abort
    // handler, so guard it — cancellation has already released the stream.
    try {
      reader.releaseLock()
    } catch {
      // Reader already released via cancel(); nothing to do.
    }
  }
}
